import { logClientAuditEvent } from '../../services/auditTrailApi';
import { ensureAuditSession } from './auditSession';

const ACTIVITY_MODULE = 'ACTIVITY';
let lastTabKey = '';
const searchStore = { current: '' };
const filterStore = { current: '' };

function debounceKey(key, store, windowMs = 500) {
  if (store.current === key) return false;
  store.current = key;
  window.setTimeout(() => {
    if (store.current === key) store.current = '';
  }, windowMs);
  return true;
}

function normalizeActivityProjectId(projectId) {
  if (projectId == null) return undefined;
  const parsed = Number(projectId);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatActivityProjectLabel(ctx = {}) {
  const name = ctx.projectName?.trim?.();
  if (name) return ` in ${name}`;
  const id = normalizeActivityProjectId(ctx.projectId);
  if (id != null) return ` in project ${id}`;
  return '';
}

export function logActivityTabView(tab, projectId, projectName) {
  ensureAuditSession();
  const key = `${projectId ?? 'na'}:${tab}`;
  if (key === lastTabKey) return;
  lastTabKey = key;
  const normalizedId = normalizeActivityProjectId(projectId);
  const projectLabel = formatActivityProjectLabel({ projectId: normalizedId, projectName });
  logClientAuditEvent({
    eventType: 'ACTIVITY_TAB_VIEW',
    module: ACTIVITY_MODULE,
    entityType: 'UI',
    entityId: `tab-${tab}`,
    entityName: tab,
    projectId: normalizedId,
    projectName: projectName?.trim?.() || undefined,
    summary: `Opened ${tab} tab${projectLabel}.`,
    displayTitle: tab,
    actionDetails: {
      tab,
      projectId: normalizedId ?? null,
      projectName: projectName?.trim?.() || null,
      route: typeof window !== 'undefined' ? window.location.pathname : null,
    },
  });
}

export function logActivityPageView(page, meta = {}) {
  ensureAuditSession();
  const projectId = normalizeActivityProjectId(meta.projectId);
  const projectName = typeof meta.projectName === 'string' ? meta.projectName.trim() : undefined;
  const { projectId: _a, projectName: _b, ...rest } = meta;
  const projectLabel = formatActivityProjectLabel({ projectId, projectName });
  logClientAuditEvent({
    eventType: 'ACTIVITY_PAGE_VIEW',
    module: ACTIVITY_MODULE,
    entityType: 'UI',
    entityId: `page-${page}`,
    entityName: page,
    projectId,
    projectName,
    summary: `Opened ${page}${projectLabel}.`,
    displayTitle: page,
    actionDetails: { page, projectId: projectId ?? null, projectName: projectName ?? null, ...rest },
  });
}

export function logActivitySearch(module, query, projectId, projectName) {
  const q = String(query || '').trim();
  if (!q) return;
  const key = `${module}:${q}:${projectId ?? 'na'}`;
  if (!debounceKey(key, searchStore, 600)) return;
  const normalizedId = normalizeActivityProjectId(projectId);
  const projectLabel = formatActivityProjectLabel({ projectId: normalizedId, projectName });
  logClientAuditEvent({
    eventType: 'ACTIVITY_SEARCH',
    module: ACTIVITY_MODULE,
    entityType: 'UI',
    entityId: module,
    summary: `Searched ${module}: "${q}"${projectLabel}.`,
    displayTitle: module,
    projectId: normalizedId,
    projectName: projectName?.trim?.() || undefined,
    actionDetails: {
      module,
      query: q,
      projectId: normalizedId ?? null,
      projectName: projectName?.trim?.() || null,
    },
  });
}

export function logActivityFilterApplied(module, filters, projectId, projectName) {
  const key = `${module}:${JSON.stringify(filters)}:${projectId ?? 'na'}`;
  if (!debounceKey(key, filterStore, 600)) return;
  const normalizedId = normalizeActivityProjectId(projectId);
  const projectLabel = formatActivityProjectLabel({ projectId: normalizedId, projectName });
  logClientAuditEvent({
    eventType: 'ACTIVITY_FILTER_APPLIED',
    module: ACTIVITY_MODULE,
    entityType: 'UI',
    entityId: module,
    summary: `Applied filters on ${module}${projectLabel}.`,
    displayTitle: module,
    projectId: normalizedId,
    projectName: projectName?.trim?.() || undefined,
    actionDetails: {
      module,
      filters,
      projectId: normalizedId ?? null,
      projectName: projectName?.trim?.() || null,
    },
  });
}

export function logActivityFilterCleared(module, projectId, projectName) {
  const normalizedId = normalizeActivityProjectId(projectId);
  const projectLabel = formatActivityProjectLabel({ projectId: normalizedId, projectName });
  logClientAuditEvent({
    eventType: 'ACTIVITY_FILTER_CLEARED',
    module: ACTIVITY_MODULE,
    entityType: 'UI',
    entityId: module,
    summary: `Cleared filters on ${module}${projectLabel}.`,
    displayTitle: module,
    projectId: normalizedId,
    projectName: projectName?.trim?.() || undefined,
    actionDetails: {
      module,
      projectId: normalizedId ?? null,
      projectName: projectName?.trim?.() || null,
    },
  });
}

export function logActivityExport(type, meta = {}) {
  const projectId = normalizeActivityProjectId(meta.projectId);
  const projectName = typeof meta.projectName === 'string' ? meta.projectName.trim() : undefined;
  const { projectId: _a, projectName: _b, ...rest } = meta;
  const projectLabel = formatActivityProjectLabel({ projectId, projectName });
  logClientAuditEvent({
    eventType: 'ACTIVITY_EXPORT',
    module: ACTIVITY_MODULE,
    entityType: 'UI',
    entityId: type,
    summary: `Exported ${type}${projectLabel}.`,
    displayTitle: type,
    projectId,
    projectName,
    actionDetails: { type, projectId: projectId ?? null, projectName: projectName ?? null, ...rest },
  });
}
